import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';

import { UntilDestroy } from '@ngneat/until-destroy';

import {
    AlertService,
    ButtonColors,
    ButtonModule,
    CheckboxModule,
    LayerService,
    NumberfieldModule,
    PopupService,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { RouterModule } from '@angular/router';

import {
    SubcontractorInvoiceModel,
    ProjectModel,
    SubscriptionBillingTiming,
} from '../../../../project.models';
import { AddSubcontractorInvoices, UpdateSubcontractorInvoice } from '../../../../project.state';
import { EdDatepickerComponent } from 'src/app/shared/components/forms/ed-datepicker/ed-datepicker.component';
import Swal from 'sweetalert2';
import { convertNumber } from 'src/app/shared/all.helpers';

export enum RecurrenceType {
    MONTHLY = 'Mensuel',
    QUARTERLY = 'Trimestriel',
    HALF_YEARLY = 'Semestriel',
    YEARLY = 'Annuel',
}

@UntilDestroy()
@Component({
    selector: 'subcontracting-generate-recurrence',
    templateUrl: './subcontracting-generate-recurrence.component.html',
    styleUrls: ['./subcontracting-generate-recurrence.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
        EdDatepickerComponent,
        CheckboxModule,
        NumberfieldModule,
    ],
})
export class SubcontractorInvoiceGenerateRecurrence {
    public buttonColors = ButtonColors;

    public header: string = '';
    public errMessage: string = '';

    public project: ProjectModel | undefined;

    public billing: Partial<SubcontractorInvoiceModel> = {};
    public formGroup: FormGroup;

    public subscriptionBillingTimingOptions = [
        {
            value: SubscriptionBillingTiming.START_OF_PERIOD,
            label: SubscriptionBillingTiming.START_OF_PERIOD,
        },
        {
            value: SubscriptionBillingTiming.END_OF_PERIOD,
            label: SubscriptionBillingTiming.END_OF_PERIOD,
        },
    ];

    public recurrenceType: RecurrenceType = RecurrenceType.YEARLY;
    public recurrenceTypeOptions = Object.entries(RecurrenceType).map(([_, label]) => ({
        value: label,
        label,
    }));
    public endRecurrence: Date = new Date();

    constructor(
        private readonly layerService: LayerService,
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly alertService: AlertService,
        private readonly popupService: PopupService,
    ) {
        this.formGroup = this.formBuilder.group({
            startDate: [{ value: new Date(), required: true }],
            endDate: [{ value: null, required: false }],
            amountExclTax: [{ value: 0, required: true }],
            isSent: [{ value: false, required: false }],
            invoiceNumber: [{ value: '', required: false }],
            comment: [{ value: '', required: false }],
            subscriptionBillingTiming: [
                { value: SubscriptionBillingTiming.START_OF_PERIOD, required: true },
            ],
        });
    }

    public updateEndRecurrence(event: any) {
        this.endRecurrence = new Date(event);
    }
    public countGeneratedBillings() {
        return this.generateRecurrence().length;
    }

    public addTimeDelta(date: Date, recurrenceType: RecurrenceType) {
        const currentDate = new Date(date);
        if (recurrenceType == RecurrenceType.MONTHLY)
            currentDate.setMonth(currentDate.getMonth() + 1);
        else if (recurrenceType == RecurrenceType.QUARTERLY)
            currentDate.setMonth(currentDate.getMonth() + 4);
        else if (recurrenceType == RecurrenceType.HALF_YEARLY)
            currentDate.setMonth(currentDate.getMonth() + 6);
        else currentDate.setFullYear(currentDate.getFullYear() + 1);
        return currentDate;
    }
    public generateRecurrence() {
        let billingList: Partial<SubcontractorInvoiceModel>[] = [];
        if (!this.billing.startDate || !this.endRecurrence) return [];

        let currentStartDate = this.formGroup.value.startDate;
        let currentEndDate = this.formGroup.value.endDate;

        while (currentStartDate.getTime() <= this.endRecurrence.getTime()) {
            billingList.push({
                ...this.billing,
                startDate: new Date(currentStartDate),
                plannedDate: new Date(currentStartDate),
                invoiceNumber: '',
                isSent: false,
                endDate: currentEndDate,
            });
            currentStartDate = this.addTimeDelta(currentStartDate, this.recurrenceType);
            currentEndDate = currentEndDate
                ? this.addTimeDelta(currentEndDate, this.recurrenceType)
                : null;
        }
        return billingList;
    }

    public addSubcontractorInvoice(updateSubcontractingAmount: boolean) {
        if (!this.project) return;
        if (this.generateRecurrence().length === 0) return;

        //Generate recurrence if necessary
        let billingList = this.generateRecurrence();

        this.store
            .dispatch(
                new AddSubcontractorInvoices({
                    subcontractorInvoices: billingList,
                    projectId: this.project.id,
                    updateSubcontractingAmount,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid(
                        'Mise à jour',
                        `le planning de facturation a été mis à jour !`,
                    );
                    this.close();
                },
                error: (err: any) => {
                    console.info(err.error.message);
                    this.errMessage = err.error.message;
                    this.alertService.error('Mise à jour', err.error.message);
                },
            });
    }

    isDirty(): boolean {
        return (
            this.formGroup.value.amountExclTax === 0 ||
            !this.formGroup.value.startDate ||
            (this.formGroup.value.endDate &&
                this.formGroup.value.endDate <= this.formGroup.value.startDate) ||
            !this.endRecurrence ||
            !this.recurrenceType ||
            this.generateRecurrence().length === 0
        );
    }

    async valid(): Promise<void> {
        if (!this.project) return;
        //create
        this.errMessage = '';
        let updateSubcontractingAmount = false;

        this.billing = {
            ...this.billing,
            amountExclTax: this.formGroup.value.amountExclTax,
            startDate: this.formGroup.value.startDate,
            endDate: this.formGroup.value.endDate,
            comment: this.formGroup.value.comment,
            invoiceNumber: this.formGroup.value.invoiceNumber,
            isSent: this.formGroup.value.isSent,
            subscriptionBillingTiming: this.formGroup.value.subscriptionBillingTiming,
        };

        if (this.projectSubcontractingAmountMismatch()) {
            await new Promise((resolve, reject) => {
                const diff = this.getSum() - (this.project?.subcontractingAmount || 0);
                let htmlContent = `Souhaitez-vous mettre à jour le montant total de sous-traitance (${convertNumber(diff, 0, true)} €HT) ?`;
                let cancelButtonText = 'Non';

                if (diff > 0) {
                    htmlContent = `La planning de facturation dépasse de ${convertNumber(diff, 0, true)} €HT, le montant total total de sous-traitance sera mis à jour.`;
                    cancelButtonText = 'Retour';
                }

                this.popupService
                    .openPopup({
                        title: `Le planning ne correspond plus au montant de sous-traitance de la mission`,
                        htmlContent,
                        cancelButtonText,
                        confirmButtonText: 'Mettre à jour',
                    })
                    .then(async (result) => {
                        if (result.isConfirmed) {
                            updateSubcontractingAmount = true;
                            return resolve(null);
                        }
                        if (result.dismiss !== Swal.DismissReason.close) {
                            if (diff > 0) return reject();
                            else {
                                return resolve(null);
                            }
                        }
                    });
            });
        }

        await this.addSubcontractorInvoice(updateSubcontractingAmount);
    }

    public set params(params: { header: string; project: ProjectModel }) {
        this.header = params.header;
        this.project = params.project;

        this.billing = {
            id: undefined,
            plannedDate: new Date(),
            startDate: new Date(),
            amountExclTax: 0,
            isSent: false,
            subscriptionBillingTiming: SubscriptionBillingTiming.START_OF_PERIOD,
        };

        this.formGroup.patchValue({
            startDate: this.billing.startDate,
            endDate: this.billing.endDate,
            amountExclTax: this.billing.amountExclTax,
            isSent: this.billing.isSent,
            invoiceNumber: this.billing.invoiceNumber,
            comment: this.billing.comment,
            subscriptionBillingTiming: this.billing.subscriptionBillingTiming,
        });
    }

    public close() {
        this.layerService.close();
    }

    public getSumFormatted() {
        return convertNumber(this.getSum(), 2, false);
    }

    public getSum(): number {
        return (
            (this.project?.subcontractorInvoices
                ?.filter((b) => b.id !== this.billing?.id)
                .reduce((tot, b) => tot + b.amountExclTax, 0) ?? 0) +
            (this.generateRecurrence().reduce((tot, b) => tot + (b.amountExclTax ?? 0), 0) ?? 0)
        );
    }

    public projectSubcontractingAmountMismatch(): boolean {
        return this.getSum() !== this.project?.subcontractingAmount;
    }
}
